import type { RunRequest } from './runRequests';
import { apiRequest } from './core/client';

/**
 * GET /api/orders/today — every item assigned to Matt, from every home, in
 * one shape (automation plan §1; server src/controllers/today.js) — and the
 * one write a Today button makes: POST /actions/:id/choose.
 */

export type TodaySource = 'order' | 'ask' | 'setup_submit' | 'discrepancies' | 'desk';

export interface TodayChip {
  kind: 'due' | 'money' | 'age';
  label: string;
  /** due: days from today (negative = late). */
  days?: number;
  date?: string;
  amount?: number;
}

export interface TodayRef {
  type: 'message' | 'document' | 'draft' | 'setup_preview' | 'receiving' | 'journey' | 'product' | 'url';
  id?: string;
  url?: string;
}

export interface TodayFact {
  label: string;
  value: string;
  ref?: TodayRef;
}

export interface TodayInput {
  type: 'date' | 'string';
  required?: boolean;
  max?: number;
}

export interface TodayOption {
  key: string;
  label: string;
  consumer: 'server' | 'agent';
  /** What the button asks for before it runs (server options only). */
  inputs?: Record<string, TodayInput>;
}

export interface TodayErrand {
  steps: { text: string; link?: string; copy?: string }[];
  upload?: { doc_hint: string };
}

export interface TodayJourney {
  id: string;
  vendor: string | null;
  label: string | null;
}

export interface TodayItem {
  key: string;
  source: TodaySource;
  title: string;
  why: TodayChip[];
  facts: TodayFact[];
  options: TodayOption[];
  errand: TodayErrand | null;
  open: { label: string; href: string } | null;
  /** Agent wording, shown only on request. */
  detail: string | null;
  journey: TodayJourney | null;
  due_date: string | null;
  amount: number | null;
  since: string | null;
  /** Order packets: the journey's other open asks (ids). */
  related_asks?: string[];
  ask?: { id: string; kind: string; owner_skill: string | null; executed: unknown };
  setup_folder?: string;
  receiving?: { receipt_id: string; receiving_id: number | null };
  /** The open "Work this with Claude" session on this item's subject, if any (not on the desk item). */
  session?: RunRequest | null;
  /** The desk session item: its latest request (open or finished). */
  desk?: { request: RunRequest | null };
}

export interface TodayQueued {
  ask_id: string;
  title: string;
  journey: TodayJourney | null;
  answered_at: string;
  answer: string | null;
  run: { request_id: string | null; state: 'waiting' | 'queued' | 'running' | 'finished'; outcome?: string | null };
}

export interface TodayResponse {
  generated_at: string;
  today: string;
  counts: { items: number; overdue: number; due_soon: number; queued: number; by_source: Record<string, number> };
  items: TodayItem[];
  queued: TodayQueued[];
  elsewhere: { queue: string; label: string; href: string; count: number }[];
  problems: string[];
}

export function getToday(): Promise<TodayResponse> {
  return apiRequest({ path: '/orders/today' });
}

export interface ChooseResult {
  action: { id: string; status: string };
  executed?: { verb: string; ok: boolean; result?: { open_url?: string } | null; error?: string; message?: string };
  queued?: { request_id: string };
}

/**
 * Tap an option: a server option runs its effect now (and closes the ask
 * unless it says to stay open); an agent option or "_other" with text is
 * stored as the answer and queues the owning skill's run.
 */
export function chooseOption(
  actionId: string,
  optionKey: string,
  extra: { text?: string; inputs?: Record<string, string> } = {}
): Promise<ChooseResult> {
  return apiRequest({
    path: `/orders/actions/${actionId}/choose`,
    method: 'POST',
    body: { option_key: optionKey, ...(extra.text ? { text: extra.text } : {}), ...(extra.inputs ? { inputs: extra.inputs } : {}) },
  });
}
